import { Entity, EntityType } from './Entity';

// ============================================
// Step 7: Entity Framework - EntityManager
// ============================================

/**
 * Manages all game entities - handles adding, removing, updating, and querying
 */
class EntityManagerClass {
  private entities: Map<string, Entity> = new Map();
  private entitiesByType: Map<EntityType, Set<string>> = new Map();
  private pendingRemoval: Set<string> = new Set();

  constructor() {
    // Initialize type sets
    const types: EntityType[] = ['player', 'monster', 'boss', 'tower', 'base', 'projectile', 'treeSlot', 'drop'];
    types.forEach(type => {
      this.entitiesByType.set(type, new Set());
    });
  }

  // ============================================
  // Entity Lifecycle
  // ============================================

  /**
   * Add an entity to the manager
   */
  add(entity: Entity): void {
    if (this.entities.has(entity.id)) {
      console.warn(`[EntityManager] Entity with id ${entity.id} already exists`);
      return;
    }

    this.entities.set(entity.id, entity);
    this.entitiesByType.get(entity.type)?.add(entity.id);
  }

  /**
   * Mark an entity for removal (will be removed at end of frame)
   */
  remove(entityOrId: Entity | string): void {
    const id = typeof entityOrId === 'string' ? entityOrId : entityOrId.id;
    this.pendingRemoval.add(id);
  }

  /**
   * Immediately remove an entity
   */
  removeImmediate(entityOrId: Entity | string): void {
    const id = typeof entityOrId === 'string' ? entityOrId : entityOrId.id;
    const entity = this.entities.get(id);

    if (entity) {
      this.entitiesByType.get(entity.type)?.delete(id);
      this.entities.delete(id);
      entity.destroy();
    }
  }

  /**
   * Process pending removals
   */
  private processPendingRemovals(): void {
    this.pendingRemoval.forEach(id => {
      this.removeImmediate(id);
    });
    this.pendingRemoval.clear();
  }

  // ============================================
  // Update Loop
  // ============================================

  /**
   * Update all entities (call once per frame)
   * Updates in order: players, monsters, towers, projectiles
   */
  update(delta: number): void {
    // Update order matters for gameplay
    const updateOrder: EntityType[] = ['player', 'monster', 'boss', 'tower', 'projectile', 'base', 'treeSlot'];

    for (const type of updateOrder) {
      const ids = this.entitiesByType.get(type);
      if (!ids) continue;

      for (const id of ids) {
        const entity = this.entities.get(id);
        if (entity && entity.active && !entity.destroyed) {
          entity.update(delta);
        }
      }
    }

    // Remove destroyed entities at end of frame
    this.processPendingRemovals();

    // Also check for entities that were destroyed during update
    this.entities.forEach((entity, id) => {
      if (entity.destroyed) {
        this.pendingRemoval.add(id);
      }
    });
    this.processPendingRemovals();
  }

  // ============================================
  // Queries
  // ============================================

  /**
   * Get an entity by ID
   */
  get<T extends Entity = Entity>(id: string): T | undefined {
    return this.entities.get(id) as T | undefined;
  }

  /**
   * Get all entities of a specific type
   */
  getByType<T extends Entity>(type: EntityType): T[] {
    const ids = this.entitiesByType.get(type);
    if (!ids) return [];

    const result: T[] = [];
    ids.forEach(id => {
      const entity = this.entities.get(id);
      if (entity && entity.active && !entity.destroyed) {
        result.push(entity as T);
      }
    });
    return result;
  }

  /**
   * Get all active entities
   */
  getAll(): Entity[] {
    return Array.from(this.entities.values()).filter(e => e.active && !e.destroyed);
  }

  /**
   * Get count of entities by type
   */
  getCount(type?: EntityType): number {
    if (type) {
      return this.entitiesByType.get(type)?.size || 0;
    }
    return this.entities.size;
  }

  /**
   * Find entities within range of a point
   */
  findInRange(
    x: number,
    y: number,
    range: number,
    type?: EntityType
  ): Entity[] {
    const rangeSquared = range * range;
    const entities = type ? this.getByType(type) : this.getAll();

    return entities.filter(entity => {
      const dx = entity.x - x;
      const dy = entity.y - y;
      return dx * dx + dy * dy <= rangeSquared;
    });
  }

  /**
   * Find the nearest entity to a point
   */
  findNearest(
    x: number,
    y: number,
    type?: EntityType,
    maxRange?: number
  ): Entity | null {
    const entities = type ? this.getByType(type) : this.getAll();
    const maxRangeSquared = maxRange ? maxRange * maxRange : Infinity;

    let nearest: Entity | null = null;
    let nearestDistSq = Infinity;

    for (const entity of entities) {
      const dx = entity.x - x;
      const dy = entity.y - y;
      const distSq = dx * dx + dy * dy;

      if (distSq < nearestDistSq && distSq <= maxRangeSquared) {
        nearestDistSq = distSq;
        nearest = entity;
      }
    }

    return nearest;
  }

  /**
   * Check if any entity exists at a tile position
   */
  hasEntityAt(tileX: number, tileY: number, type?: EntityType, tileSize: number = 64): boolean {
    const worldX = (tileX + 0.5) * tileSize;
    const worldY = (tileY + 0.5) * tileSize;
    const entities = this.findInRange(worldX, worldY, tileSize / 2, type);
    return entities.length > 0;
  }

  // ============================================
  // Cleanup
  // ============================================

  /**
   * Remove all entities
   */
  clear(): void {
    this.entities.forEach(entity => entity.destroy());
    this.entities.clear();
    this.entitiesByType.forEach(set => set.clear());
    this.pendingRemoval.clear();
  }

  /**
   * Remove all entities of a specific type
   */
  clearType(type: EntityType): void {
    const ids = this.entitiesByType.get(type);
    if (!ids) return;

    ids.forEach(id => {
      const entity = this.entities.get(id);
      if (entity) {
        entity.destroy();
        this.entities.delete(id);
      }
    });
    ids.clear();
  }
}

// Singleton instance
export const EntityManager = new EntityManagerClass();
